import React, { useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { KeyboardArrowUp, KeyboardArrowDown, LocalOffer } from '@mui/icons-material';
import Slider from 'react-slick';
import { useNavigate } from 'react-router-dom';
import { useDeals } from '../hooks/useDeals';
import DealCard from './DealCard';
import { Deal } from '../services/deals';

const DealsCarousel: React.FC = () => {
  const sliderRef = useRef<any>(null);
  const navigate = useNavigate();
  const { freeGames, loadingFreeGames, lastUpdated } = useDeals();

  // Utiliser tous les jeux disponibles sans limitation
  const deals = useMemo(() => {
    return freeGames.slice(0, 10);
  }, [freeGames]);

  // Dupliquer les items si nécessaire pour le loop infini
  const extendedDeals = useMemo(() => {
    if (!deals.length) return [];
    if (deals.length < 4) {
      return [...deals, ...deals, ...deals, ...deals].slice(0, 12);
    }
    return deals;
  }, [deals]);

  const sliderSettings = {
    infinite: true,
    vertical: true,
    verticalSwiping: true,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    speed: 800,
    autoplaySpeed: 4000,
    cssEase: "ease-in-out",
    pauseOnHover: true,
    arrows: false,
    dots: false,
  };

  // Gestion du scroll à la molette
  const handleWheel = (e: React.WheelEvent) => {
    // Empêche le scroll de la page parente
    e.stopPropagation();

    // Si on scrolle vite (deltaY grand), on peut vouloir scroller plusieurs items
    const scrollThreshold = 20; // Seuil pour déclencher un scroll

    // Détecter la direction
    if (e.deltaY < 0) {
      // Scroll UP
      if (Math.abs(e.deltaY) > 50) {
        // Scroll rapide -> 2 items
        sliderRef.current?.slickGoTo(sliderRef.current?.innerSlider?.state.currentSlide - 2);
      } else {
        sliderRef.current?.slickPrev();
      }
    } else {
      // Scroll DOWN
      if (Math.abs(e.deltaY) > 50) {
        // Scroll rapide -> 2 items
        sliderRef.current?.slickGoTo(sliderRef.current?.innerSlider?.state.currentSlide + 2);
      } else {
        sliderRef.current?.slickNext();
      }
    }
  };

  const handleDealClick = (deal: Deal) => {
    if (deal.otherDeals && deal.otherDeals.length > 0) {
      navigate('/deals');
    } else {
      window.open(deal.url, '_blank', 'noopener,noreferrer');
    }
  };

  if (loadingFreeGames) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, height: '100%', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (deals.length === 0) {
    return null;
  }

  return (
    <Paper
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        py: 2,
        px: 1,
        backgroundColor: 'rgba(30, 30, 30, 0.7)',
        borderRadius: 2,
        overflow: 'hidden',
        // Empêche le scroll parent quand la souris est dessus (supporté par la plupart des navigateurs modernes)
        overscrollBehavior: 'contain'
      }}
    >
      {/* Header Titre */}
      <Box
        onClick={() => navigate('/deals')}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          mb: 2,
          color: 'white',
          bgcolor: 'rgba(0,0,0,0.5)',
          py: 1,
          px: 1,
          borderRadius: 2,
          flexShrink: 0,
          cursor: 'pointer',
          transition: 'background-color 0.2s',
          '&:hover': {
            bgcolor: 'rgba(0,0,0,0.7)'
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalOffer color="primary" />
          <Typography variant="h6" fontWeight="bold">
            Bons Plans
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ color: '#aaa', textAlign: 'center' }}>
          {freeGames.length > deals.length
            ? `Voir les ${freeGames.length - deals.length} autres bons plans`
            : `${freeGames.length} bons plans`
          }
          {lastUpdated && (
            <span style={{ display: 'block', fontStyle: 'italic', fontSize: '0.7rem' }}>
              Màj : {lastUpdated.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} {lastUpdated.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </Typography>
      </Box>

      {/* Bouton Haut */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        mb: 1,
        flexShrink: 0
      }}>
        <IconButton
          onClick={() => sliderRef.current?.slickPrev()}
          sx={{
            color: '#ffffff',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            width: 32,
            height: 32,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              borderColor: '#ffffff',
              transform: 'scale(1.1)'
            },
            transition: 'all 0.2s ease-in-out'
          }}
        >
          <KeyboardArrowUp />
        </IconButton>
      </Box>

      {/* Zone slider contrainte physiquement entre les boutons */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'hidden',
          position: 'relative',
          mx: 0.5,
        }}
        onWheel={handleWheel}
      >
        <Box sx={{
          height: '100%',
          '& .slick-slider, & .slick-list, & .slick-track': { height: '100% !important' },
          '& .slick-slide': { pb: 2, px: 0.5 }
        }}>
          <Slider ref={sliderRef} {...sliderSettings}>
            {extendedDeals.map((deal, idx) => (
              <Box key={`${deal.title}-${idx}`}>
                <DealCard
                  deal={deal}
                  onClick={handleDealClick}
                  sx={{ height: 180 }}
                />
              </Box>
            ))}
          </Slider>
        </Box>
      </Box>

      {/* Bouton Bas */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        mt: 1,
        flexShrink: 0
      }}>
        <IconButton
          onClick={() => sliderRef.current?.slickNext()}
          sx={{
            color: '#ffffff',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            width: 32,
            height: 32,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              borderColor: '#ffffff',
              transform: 'scale(1.1)'
            },
            transition: 'all 0.2s ease-in-out'
          }}
        >
          <KeyboardArrowDown />
        </IconButton>
      </Box>
    </Paper>
  );
};

export default React.memo(DealsCarousel);
